import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  IconButton,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/system';
import { parseBlob } from 'music-metadata';
import { useSnackbar } from 'notistack';
import React, { useCallback, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IEmoji } from 'src/@types/chat';
import { useConfirmationModal } from 'src/components/ConfirmationModal';
import { EmojiPopover } from 'src/components/EmojiPickerPopover';
import { PopoverProps } from 'src/components/PopoverTrigger';
import { RHFSlider, RHFTextField } from 'src/components/hook-form';
import FormProvider from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { SOUNDBOARD_CDN } from 'src/config-global';
import { useLocales } from 'src/locales';
import { SoundBoardData } from 'src/requests/soundboard';
import * as Yup from 'yup';
import { useAddSoundMutation } from './useSoundBoardAddMutation';
import { useUpdateSoundMutation } from './useSoundBoardUpdateMutation';
import { useDeleteSoundMutation } from './usseSoundBoardDeleteMutation';

type FormValuesProps = {
  name: string;
  icon: string;
  volume: number;
  sound: File;
};

export const SoundBoardAddEditSoundPopover = ({
  target,
  handleClose,
  sound,
}: PopoverProps<{ sound?: SoundBoardData }>) => {
  const open = Boolean(target);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const emojiRef = useRef<any>(null);
  const [playableAudio, setPlayableAudio] = useState<string>(
    !!sound ? `${SOUNDBOARD_CDN}${sound?._id}` : '',
  );

  const { translate } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const { confirm } = useConfirmationModal();

  const { addSound } = useAddSoundMutation();
  const { updateSound } = useUpdateSoundMutation();
  const { deleteSound, isDeletingSoundLoading } = useDeleteSoundMutation();

  const FormSchema = Yup.object().shape({
    name: Yup.string().required(translate('shared.yup.name_is_required')),
    icon: Yup.string().required(translate('shared.yup.icon_is_required')),
    volume: Yup.number(),
    sound: Yup.mixed<File>()
      .test('fileSize', translate('shared.yup.field_is_required'), (value) => {
        if (!!sound) return true;
        if (!value) return false;
        return !!sound || value.size > 0;
      })
      .test(
        'fileLength',
        translate('shared.yup.sound_length'),
        async (value) => {
          if (!!sound) return true;
          if (!value?.type) return false;
          const metadata = await parseBlob(value);
          return metadata && (metadata?.format?.duration || 0) <= 6;
        },
      ),
  });

  const methods = useForm({
    resolver: yupResolver(FormSchema),
    defaultValues: {
      name: sound?.name || '',
      icon: sound?.icon || '',
      volume: sound?.volume || 0.5,
      sound: new File([], ''),
    },
  });

  const handleUpdateSound = async (data: FormValuesProps) => {
    const { name, icon, volume } = data;
    await updateSound({
      soundId: sound?._id!,
      name,
      icon,
      volume,
    });
  };

  const handleCreateSound = async (data: FormValuesProps) => {
    const { sound, name, icon, volume } = data;

    await addSound({
      sound,
      name,
      icon,
      volume,
    });
  };

  const handleSubmit = methods.handleSubmit(async (data) => {
    !!sound ? await handleUpdateSound(data) : await handleCreateSound(data);
    handleClose();
  });

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const metadata = await parseBlob(file);

    if (!metadata || (metadata?.format?.duration || 0) > 6) {
      enqueueSnackbar(translate('shared.snackbar.sound_invalid'));
      return;
    }

    setPlayableAudio(URL.createObjectURL(file));
    methods.setValue('sound', file, { shouldDirty: true });
  };

  const handleClickAttachSound = useCallback(() => {
    audioInputRef.current?.click();
  }, []);

  const handleEmojiClick = useCallback(() => {
    emojiRef.current?.click();
  }, []);

  const handleSelectEmoji = (icon: IEmoji) => {
    methods.setValue('icon', icon.shortcodes, { shouldDirty: true });
  };

  const handlePlayAudio = () => {
    let audio = new Audio(playableAudio);
    audio.volume = volumeValue;
    audio.play().then();
  };

  const handleDelete = async () => {
    if (!sound) return;
    if (
      !(await confirm({
        title: translate('shared.confirmation.are_you_sure_title'),
        message: translate('shared.confirmation.are_you_sure_delete_content', {
          contentName: sound.name,
        }),
      }))
    )
      return;

    await deleteSound({ soundId: sound._id });
  };

  const iconValue = methods.watch('icon');
  const soundValue = methods.watch('sound');
  const volumeValue = methods.watch('volume');

  return (
    <StyledPopover
      open={open}
      anchorEl={target}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      onClick={(e) => e.stopPropagation()}
    >
      <FormProvider methods={methods} onSubmit={handleSubmit}>
        <Stack gap={2}>
          {!sound && (
            <>
              <Stack gap={1}>
                <Stack direction="row" alignItems="baseline" gap={0.5}>
                  <Typography variant="subtitle2">
                    {translate('communicate.sound_board.sound_file')}:{' '}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {translate('communicate.sound_board.max_sound_length', {
                      seconds: 6,
                    })}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={2}>
                  <TextField
                    disabled
                    value={soundValue?.name}
                    onClick={handleClickAttachSound}
                    size="small"
                  />
                  <Button
                    variant="contained"
                    onClick={handleClickAttachSound}
                    disabled={!!sound}
                  >
                    {translate('communicate.sound_board.select_audio')}
                  </Button>
                </Stack>
              </Stack>
              <input
                type="file"
                ref={audioInputRef}
                style={{ display: 'none' }}
                accept="audio/*"
                onChange={(evt) => handleFileSelect(evt)}
              />
            </>
          )}

          <Stack direction="row" alignItems="center" gap={2}>
            <Stack gap={1}>
              <Typography variant="subtitle2">
                {translate('communicate.sound_board.sound_name')}:
              </Typography>
              <RHFTextField name="name" size="small" />
            </Stack>

            <Stack gap={1}>
              <Typography variant="subtitle2">Emoji:</Typography>
              <StyledEmojiContainer
                direction="row"
                alignItems="center"
                {...methods.register('icon')}
              >
                {!!iconValue && (
                  <em-emoji shortcodes={iconValue} onClick={handleEmojiClick} />
                )}
                <EmojiPopover
                  ref={emojiRef}
                  onSelectEmoji={(emoji) => handleSelectEmoji(emoji)}
                  tooltipText={translate(
                    'communicate.sound_board.select_emoji',
                  )}
                  sx={{
                    visibility: iconValue ? 'hidden' : 'visible',
                    p: 0,
                    '& svg': {
                      width: 40,
                      height: 40,
                    },
                  }}
                />
              </StyledEmojiContainer>
            </Stack>
          </Stack>

          <Stack gap={1}>
            <Stack direction="row" alignItems="center" gap={2} height={32}>
              <Typography variant="subtitle2">
                {translate('communicate.sound_board.sound_volume')}:
              </Typography>
              {playableAudio && (
                <StyledPlayerButton
                  onClick={handlePlayAudio}
                  size="small"
                  color="primary"
                >
                  <Iconify icon="tabler:player-play-filled" />
                </StyledPlayerButton>
              )}
            </Stack>
            <RHFSlider name="volume" min={0} max={1} step={0.1} marks />
          </Stack>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          gap={2}
          mt={2}
        >
          {!!sound && (
            <IconButton
              disabled={
                methods.formState.isSubmitting || isDeletingSoundLoading
              }
              onClick={handleDelete}
              color="error"
            >
              <Iconify icon="tabler:trash" />
            </IconButton>
          )}
          <Button
            disabled={methods.formState.isSubmitting || isDeletingSoundLoading}
            onClick={handleClose}
          >
            {translate('shared.buttons.cancel')}
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            disabled={!!sound && !methods.formState.isDirty}
            loading={methods.formState.isSubmitting || isDeletingSoundLoading}
          >
            {translate(
              !!sound
                ? 'communicate.sound_board.update_sound'
                : 'communicate.sound_board.add_sound',
            )}
          </LoadingButton>
        </Stack>
      </FormProvider>
    </StyledPopover>
  );
};

const StyledPopover = styled(Popover)(({ theme }) => ({
  '& .MuiPaper-root': {
    padding: theme.spacing(2),
  },
}));

const StyledEmojiContainer = styled(Stack)(({ theme }) => ({
  '& em-emoji': {
    height: theme.spacing(5),
    paddingTop: theme.spacing(0.25),
    cursor: 'pointer',
    '& .emoji-mart-emoji': {
      '& span': {
        fontSize: 38,
        lineHeight: 1,
      },
    },
  },
}));

const StyledPlayerButton = styled(IconButton)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  '& svg': {
    width: theme.spacing(2),
  },
}));
